/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {IdTokenClaims} from 'oidc-client-ts';

/**
 * Provides the ordered list of ID-token claims from which the authenticated
 * user's username is resolved. Okta asserts `email`; Microsoft Entra asserts
 * `preferred_username` or `upn` when the tenant account carries no mailbox.
 */
const USERNAME_CLAIMS = ['email', 'preferred_username', 'upn'];

/**
 * Resolves the authenticated user's username from the identity provider's
 * ID-token claims, returning the first populated claim in preference order.
 *
 * ```typescript
 *   import {resolveUsernameClaim} from '@ion/applications/src/authentication/OidcClaims';
 *   ...
 *
 *   const username = resolveUsernameClaim(user.profile);
 *
 * ```
 *
 * @param   claims The ID-token claims asserted by the identity provider.
 * @returns The resolved username, otherwise `undefined` when no claim carries one.
 */
export const resolveUsernameClaim = (claims: IdTokenClaims): string | undefined => (
  USERNAME_CLAIMS
    .map((claim) => claims?.[claim])
    .find((value) => typeof value === 'string' && value !== '') as string | undefined
);

/**
 * Determines whether the identity asserted by the provider matches the username
 * the user authenticated with, compared case-insensitively.
 *
 * ```typescript
 *   import {matchesAuthenticatedUser} from '@ion/applications/src/authentication/OidcClaims';
 *   ...
 *
 *   const isValid = matchesAuthenticatedUser(user.profile, 'tonydanza@vzconnect.com');
 *
 * ```
 *
 * @param   claims The ID-token claims asserted by the identity provider.
 * @param   username The username the login attempt was started with.
 * @returns `true` when the asserted identity matches, otherwise `false`.
 */
export const matchesAuthenticatedUser = (claims: IdTokenClaims, username: string): boolean => {
  const resolved = resolveUsernameClaim(claims);
  return Boolean(resolved && username && resolved.toLowerCase() === username.toLowerCase());
};
